import React, { useState } from 'react';
import { Linking, TouchableOpacity, useWindowDimensions, View } from 'react-native'

import tw from '@/styles/tailwind';
import Text from '@/components/Text';
import IcomoonIcon from '@/components/IcomoonIcon';
import { Illustration } from '@/components/Illustration';
import api from '@/utils/api';
import { useQuery } from '@tanstack/react-query';
import { useNavigation } from '@react-navigation/native';

import dayjs from 'dayjs'
import 'dayjs/locale/es'
import { Image } from 'expo-image';
import { useSelector } from 'react-redux';
import PaymentRequestApprovalRejectBottomSheet from './PaymentRequestApprovalRejectBottomSheet';
import useFormSubmit from '@/hooks/useFormSubmit';
import DocumentPicker from './components/DocumentPicker';
import { useToast } from 'react-native-toast-notifications';
import PrintDocumentsModal from './components/PrintDocumentsModal';


// Card blanca con título a la izquierda (estilo del diseño) y acción opcional a la derecha.
function Card({ title, right, children, style }: any) {
  return (
    <View style={tw.style(`bg-white rounded-xl border border-gray-100 px-5 py-4`, style)}>
      {(title || right) && (
        <View style={tw`row justify-between items-center mb-3`}>
          {title ? <Text style={tw`text-gray-800 font-600 text-base`}>{title}</Text> : <View />}
          {right}
        </View>
      )}
      {children}
    </View>
  )
}


function AttributeRow({ label, value, last }: any) {
  return (
    <View style={tw.style(`row justify-between py-2.5`, !last && `border-b border-gray-100`)}>
      <Text style={tw`text-gray-500`}>{label}</Text>
      <Text style={tw`text-gray-800 font-500 text-right flex-shrink ml-3`}>{value}</Text>
    </View>
  )
}


function Currency({ value, style }: any) {
  function format(numero: any) {
    let partes = Number(numero || 0).toFixed(2).split('.');
    partes[0] = partes[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return partes.join('.');
  }

  const _value = format(value)

  return (
    <Text style={style}>${_value}</Text>
  )
}


// Badge de estatus derivado de los approvals (no hay campo de prioridad en el modelo).
function StatusBadge({ approvals }: any) {
  let label = 'Pendiente'
  let color = 'gray-500'
  let bg = 'gray-100'
  let dot = 'gray-400'

  if (approvals?.some((a: any) => a.status === 'rejected')) {
    label = 'Rechazado'; color = 'red-600'; bg = 'red-50'; dot = 'red-500'
  } else if (approvals?.some((a: any) => a.status === 'in_progress')) {
    label = 'En proceso'; color = 'amber-600'; bg = 'amber-50'; dot = 'amber-500'
  } else if (approvals?.length && approvals.every((a: any) => a.status === 'approved')) {
    label = 'Avalado'; color = 'primary'; bg = 'primary/10'; dot = 'primary'
  }

  return (
    <View style={tw.style(`row items-center gap-x-1.5 px-3 py-0.5 rounded-full bg-${bg}`)}>
      <View style={tw`h-1.5 w-1.5 rounded-full bg-${dot}`} />
      <Text style={tw`text-${color} text-sm`}>{label}</Text>
    </View>
  )
}


export function ApprovalFlowRow({ step, hasNext }: any){
  if(!step) return null

  // titleColor: color del nombre del paso. circle: cómo se dibuja el indicador.
  // lineColor: color del conector vertical hacia el siguiente paso.
  let titleColor = 'gray-400'
  let lineColor = 'gray-200'
  let badgeLabel, badgeColor, badgeBg
  let circle

  switch (step.status) {
    case 'approved':
      titleColor = 'gray-900'
      lineColor = 'primary'
      badgeLabel = 'Avalado'; badgeColor = 'primary'; badgeBg = 'primary/10'
      circle = (
        <View style={tw`h-7 w-7 rounded-full bg-primary justify-center items-center`}>
          <IcomoonIcon name='check' color={'#fff'} size={14} />
        </View>
      )
      break;

    case 'in_progress':
      titleColor = 'gray-900'
      lineColor = 'gray-200'
      badgeLabel = 'En proceso'; badgeColor = 'amber-600'; badgeBg = 'amber-50'
      circle = (
        <View style={tw`h-7 w-7 rounded-full border-2 border-amber-500 justify-center items-center`}>
          <View style={tw`h-2.5 w-2.5 rounded-full bg-amber-500`} />
        </View>
      )
      break;

    case 'rejected':
      titleColor = 'red-600'
      lineColor = 'red-500'
      badgeLabel = 'Rechazado'; badgeColor = 'red-600'; badgeBg = 'red-50'
      circle = (
        <View style={tw`h-7 w-7 rounded-full bg-red-500 justify-center items-center`}>
          <IcomoonIcon name='cancel' color={'#fff'} size={14} />
        </View>
      )
      break;

    case 'pending':
    default:
      titleColor = 'gray-400'
      lineColor = 'gray-200'
      badgeLabel = 'Pendiente'; badgeColor = 'gray-500'; badgeBg = 'gray-100'
      circle = (
        <View style={tw`h-7 w-7 rounded-full border-2 border-gray-300 justify-center items-center`}>
          <View style={tw`h-1.5 w-1.5 rounded-full bg-gray-300`} />
        </View>
      )
      break;
  }

  // reviewed_at es datetime -> mostramos fecha y hora como en el diseño.
  const reviewedAtStr = step.reviewed_at
    ? `${dayjs(step.reviewed_at).format('DD/MMM/YYYY')} · ${dayjs(step.reviewed_at).format('H:mm')} hrs`
    : null

  // Línea de responsable: "Nombre · fecha · hora", o "Sin asignar" si aún no se revisa.
  const hasMeta = !!(step.reviewer_name || reviewedAtStr)
  const metaLine = step.reviewer_name
    ? `${step.reviewer_name}${reviewedAtStr ? ` · ${reviewedAtStr}` : ''}`
    : (reviewedAtStr || 'Sin asignar')

  return (
    <View style={tw`row items-stretch`}>
      <View style={tw`items-center`}>
        {circle}

        {/* linea vertical conectora */}
        {
          hasNext &&
            <View style={tw`flex-1 mt-1 w-0.5 bg-${lineColor}`}></View>
        }
      </View>

      <View style={tw.style(`flex-1 px-3`, hasNext ? `pb-6` : `pb-1`)}>
        <View style={tw`row items-center gap-x-2`}>
          <Text style={tw`text-base font-600 text-${titleColor}`}>{step.step_name}</Text>
          {
            badgeLabel &&
              <View style={tw.style(`px-2 py-0.5 rounded-full bg-${badgeBg}`)}>
                <Text style={tw`text-${badgeColor} text-xs`}>{badgeLabel}</Text>
              </View>
          }
        </View>
        <Text style={tw`text-sm text-gray-500 mt-0.5`}>{step.step_description}</Text>
        <Text style={tw.style(`text-xs text-gray-400 mt-1`, !hasMeta && `italic`)}>{metaLine}</Text>
        {
          step.comments &&
            <Text style={tw`text-sm font-500 mt-0.5`}>{step.comments}</Text>
        }
      </View>
    </View>
  )
}

function ApprovalFlow({ paymentRequest }: any){
  return (
    <Card title="Trazabilidad del pago">
      <ApprovalFlowRow
        hasNext={true}
        step={{
          step_name: "Envío de solicitud", step_description: "Alta de factura", reviewed_at: paymentRequest.created_at, status: 'approved'
        }}
      />
      {
        paymentRequest.approvals.map((approvalStep: any, i: number) => {
          return (
            <ApprovalFlowRow
              hasNext={i<paymentRequest.approvals.length-1}
              key={i}
              step={approvalStep}
            />
          )
        })
      }
    </Card>
  )
}


function FileButton({url, type, name, subtitle}: any){
  return (
    <View style={tw`row items-center py-3 border-b border-gray-100`}>
      {
        type == 'image' &&
          <Image source={url} style={tw`h-12 w-10 rounded`} contentFit='cover' />
      }
      {
        type == 'xml' &&
          <Illustration name="iconXml" width={48} height={48} style={tw`self-center`} />
      }
      {
        type == 'pdf' &&
          <Illustration name="pdfIcon" width={48} height={48} style={tw`self-center`} />
      }
      {
        type == 'zip' &&
          <Illustration name="iconFile" width={48} height={48} style={tw`self-center`} />
      }

      <View style={tw`flex-1 ml-3`}>
        <Text style={tw`text-gray-800 font-500`}>{name}</Text>
        <Text style={tw`text-sm text-gray-400`}>{subtitle}</Text>
      </View>

      <TouchableOpacity
        style={tw`px-3 py-1.5 rounded-lg border border-gray-200 mr-2`}
        onPress={() => Linking.openURL(url)}
      >
        <Text style={tw`text-secondary text-sm`}>Ver</Text>
      </TouchableOpacity>

      <TouchableOpacity
        style={tw`px-3 py-1.5 rounded-lg border border-gray-200`}
        onPress={() => Linking.openURL(url)}
      >
        <Text style={tw`text-secondary text-sm`}>Descargar</Text>
      </TouchableOpacity>
    </View>
  )
}


export default function PaymentRequestDetailScreen({ route }) {
  const { params: { paymentRequestId, canReview } } = route
  const [showRejectModal, setShowRejectModal] = useState(false);

  const { roles, global_roles } = useSelector(state => state.status)
  const [showCfdiValidationDetails, setShowCfdiValidationDetails] = useState(false);
  const [showPrintDocumentsModal, setShowPrintDocumentsModal] = useState(false);

  const navigation = useNavigation()
  const { width } = useWindowDimensions()
  const twoCol = width > 1100

  const { isInitialLoading, error, data: paymentRequest, refetch } = useQuery({
    queryKey: ['vinte.payment-requests.detail', paymentRequestId],
    queryFn: () => api.get(`/vinte/payment-requests/${paymentRequestId}`).then(response => response.data),
  })


  const is_despacho = global_roles?.some(e => e.name == "vinte_despacho")
  const is_regional = global_roles?.some(e => e.name == "vinte_corporativo_posventa")
  const current_approval_step = paymentRequest?.approvals.find(e => e.status == "in_progress")

  //!!
  const can_approve =
      // canReview !== false //!?
      true
      && (
        is_despacho
        ||
        is_regional
        ||
        global_roles?.some(e => e.name == current_approval_step?.role.name)
        ||
        roles?.some(e => e.role.name == current_approval_step?.role.name)
      )


  console.log({ paymentRequest, global_roles, roles, current_approval_step });



  const { submitForm, isSubmitting } = useFormSubmit({
    onSuccess: () => {
      refetch()
    },
    onError: (error) => {
      console.log(error)
    },
    successToastMessage: `Pago aprobado exitosamente`,
    endpoint: `/vinte/payment-requests/${paymentRequestId}/approval-review`,
  })

  const submitReview = (
    veredict:"approved"|"rejected",
    comments?: string | null
  ) => {
    const data = { comments, veredict }

    console.log("submittionRegas", { comments, veredict })

    submitForm(data)
  }

  const approve = () => {
    submitReview("approved")
  }

  const rejectPaymentRequest = (comments=null) => {
    submitReview("rejected", comments)
  }


  const toast = useToast()


  if(! paymentRequest) return <Text>Cargando...</Text>

  const {cfdi} = paymentRequest

  // "Proveedor + concepto debajo" para el título del header.
  const providerName = cfdi?.cfdi_data?.emisor?.nombre || paymentRequest.provider?.name

  // PLACEHOLDER: no hay campo de urgencia en PaymentRequest todavía; se conectará después.
  const isUrgent = false

  // Breadcrumb: fraccionamiento / categoría de gasto (parent), en mayúsculas.
  const breadcrumbCategory = paymentRequest.expense_parent_type?.name

  // Fecha larga en español: "Miércoles 3 de junio 2026".
  const longDate = (() => {
    const d = dayjs(paymentRequest.date).locale('es').format('dddd D [de] MMMM YYYY')
    return d.charAt(0).toUpperCase() + d.slice(1)
  })()

  const goToProvider = () => {
    if (!paymentRequest.provider?.uuid) return
    (navigation as any).navigate("Vinte.Providers.Detail", { providerId: paymentRequest.provider.uuid })
  }

  return (
    <View style={tw`p-6 gap-y-5`}>
      {/*

        Header card: proveedor + concepto + monto

      */}
      <View style={tw`bg-white rounded-xl border border-gray-100 overflow-hidden`}>
        {/* Barra de acento (gradiente) */}
        <View style={{ height: 5, backgroundImage: 'linear-gradient(90deg, #5B2A9D 0%, #7C3AED 55%, #C4B5FD 100%)' } as any} />

        <View style={tw`px-5 py-4`}>
          {/* Fila superior: breadcrumb + badges */}
          <View style={tw`row justify-between items-center mb-3`}>
            <View style={tw`row items-center flex-1 pr-3`}>
              <Text style={tw`text-secondary text-xs font-700`}>{String(paymentRequest.community.name || '').toUpperCase()}</Text>
              {
                breadcrumbCategory &&
                  <>
                    <Text style={tw`text-gray-300 text-xs mx-2`}>/</Text>
                    <Text style={tw`text-gray-400 text-xs font-600`} numberOfLines={1}>{String(breadcrumbCategory).toUpperCase()}</Text>
                  </>
              }
            </View>

            <View style={tw`row items-center gap-x-2`}>
              {
                isUrgent &&
                  <View style={tw`row items-center gap-x-1 px-3 py-0.5 rounded-full bg-red-50`}>
                    <Text style={tw`text-red-500 text-xs`}>⚡</Text>
                    <Text style={tw`text-red-600 text-sm`}>Urgente</Text>
                  </View>
              }
              <StatusBadge approvals={paymentRequest.approvals} />
            </View>
          </View>

          {/* Fila principal: contenido + divisor + monto */}
          <View style={tw`row justify-between items-stretch`}>
            <View style={tw`flex-1 pr-4`}>
              {/* Folio */}
              <Text style={tw`text-secondary text-sm mb-1`}>Folio {paymentRequest.request_id}</Text>

              {/* Título = proveedor */}
              <Text style={tw`text-xl font-700 text-gray-900`}>{providerName}</Text>

              {/* Concepto */}
              {
                paymentRequest.description &&
                  <Text style={tw`text-gray-500 mt-1`}>{paymentRequest.description}</Text>
              }

              {/* Meta: fecha + tipo de soporte */}
              <View style={tw`row items-center gap-x-4 mt-3`}>
                <View style={tw`row items-center`}>
                  <Text style={tw`mr-1.5`}>📅</Text>
                  <Text style={tw`text-gray-500 text-sm`}>{longDate}</Text>
                </View>
                <View style={tw`row items-center`}>
                  <Text style={tw`mr-1.5`}>📄</Text>
                  <Text style={tw`text-gray-500 text-sm`}>Factura</Text>
                </View>
              </View>
            </View>

            {/* Divisor vertical */}
            <View style={tw`w-px bg-gray-200 self-stretch mx-4`} />

            {/* Monto */}
            <View style={tw`items-end justify-center`}>
              <Text style={tw`text-gray-400 text-xs font-600`}>POR PAGAR</Text>
              <Currency value={cfdi?.cfdi_data?.monto_total} style={tw`text-3xl font-700 text-secondary mt-1`} />
              {
                cfdi?.cfdi_data?.monto_total_letra &&
                  <Text style={tw`text-gray-400 text-sm italic mt-1 text-right`}>{cfdi.cfdi_data.monto_total_letra}</Text>
              }
            </View>
          </View>
        </View>
      </View>


      {/*

        2 columnas: Trazabilidad | Detalles + Cuenta destino

      */}
      <View style={tw.style(twoCol ? `flex-row gap-x-5` : `flex-col gap-y-5`)}>
        {/* Columna izquierda */}
        <View style={tw.style(`gap-y-5`, twoCol ? `flex-1` : `w-full`)}>
          <ApprovalFlow paymentRequest={paymentRequest} />


          {/*

            Facturación / validación CFDI

          */}
          <Card title="CFDI y validación fiscal">
            <AttributeRow label={"0002928353"} value={"✅"} />

            <View style={tw`row justify-between mt-3 mb-1 border-t border-b border-gray-200 py-3`}>
              <View style={tw`row`}>
                <Text style={tw`text-primary`}>9/6/2025</Text>
                <Text style={tw`text-primary ml-3`}>CFDI Válido</Text>
              </View>

              <TouchableOpacity style={tw`row`} onPress={() => setShowCfdiValidationDetails(v => !v)}>
                <Text style={tw`text-primary`}>Detalles </Text>
                <IcomoonIcon name='chevron-right' color={tw.color('text-primary')} style={tw`ml-1`} />
              </TouchableOpacity>
            </View>


            {
              showCfdiValidationDetails &&
              <View style={tw`mt-2`}>
                <View style={tw``}>
                  {
                    cfdi.validation_response?.detalleMensajes?.map((msg: any) =>
                      <Text style={tw`text-sm text-primary`}>
                        <IcomoonIcon name='check' color={tw.color('text-primary')} size={14} style={tw`mr-1`} />
                        {msg}
                      </Text>
                    )
                  }
                </View>

                <TouchableOpacity style={tw`bg-primary rounded-full items-center justify-between py-2 self-center px-5 mt-3`}>
                  <Text style={tw`text-base text-white`}>Validar Nuevamente</Text>
                </TouchableOpacity>
              </View>
            }
          </Card>
        </View>

        {/* Columna derecha */}
        <View style={tw.style(`gap-y-5`, twoCol ? `flex-1` : `w-full`)}>
          {/*
            Detalles generales
          */}
          <Card title="Detalles">
            <AttributeRow label={"Tipo de soporte"} value={"Factura"} />

            <View style={tw`mb-3 mt-3`}>
              <Text style={tw`text-gray-500`}>Tipo de gasto</Text>
              <Text style={tw`text-gray-800`}>{paymentRequest.expense_parent_type.name}</Text>
              <Text style={tw`text-gray-500`}>{paymentRequest.expense_type.name}</Text>
            </View>

            <AttributeRow label={"Número de solicitud"} value={paymentRequest.request_id} />

            <View style={tw`mt-3`}>
              <Text style={tw`text-gray-400`}>Concepto</Text>
              <Text style={tw`text-gray-700`}>{paymentRequest.description}</Text>
            </View>
          </Card>


          {/*
            Datos cuenta destino
          */}
          <Card title="Datos de la cuenta destino">
            <AttributeRow label={"A favor de"} value={cfdi.cfdi_data?.emisor?.nombre} />
            <AttributeRow label={"RFC"} value={cfdi.cfdi_data?.emisor?.rfc} />
            <AttributeRow label={"Tipo de proveedor"} value={"Servicio"} />
            <AttributeRow label={"Persona"} value={"Física"} />

            <AttributeRow label={"Banco"} value={paymentRequest.provider_bank_account?.bank} />
            <AttributeRow label={"No. de cuenta"} value={paymentRequest.provider_bank_account?.account_number} />
            <AttributeRow label={"CLABE"} value={paymentRequest.provider_bank_account?.clabe} />

            <AttributeRow label={"Fecha"} value={"Lunes 22 de mayo 2025"} />
            <AttributeRow label={"Semana"} value={"29"} last />

            {/* Caja resaltada: a favor de / cantidad a pagar */}
            <View style={tw`bg-gray-50 rounded-xl px-4 py-3 mt-3`}>
              <Text style={tw`text-gray-400 text-sm`}>A favor de</Text>
              <Text style={tw`text-gray-800 font-600`}>{cfdi.cfdi_data?.emisor?.nombre}</Text>

              <Text style={tw`text-gray-400 text-sm mt-2`}>Cantidad a pagar</Text>
              <Currency value={cfdi.cfdi_data?.monto_total} style={tw`text-xl text-secondary font-700`} />
              <Text style={tw`text-sm text-gray-500`}>{cfdi.cfdi_data?.monto_total_letra}</Text>
            </View>

            {/* Ver detalle del proveedor */}
            <TouchableOpacity
              style={tw`row justify-center items-center border border-secondary rounded-xl py-3 mt-3`}
              onPress={goToProvider}
            >
              <Text style={tw`text-secondary font-500`}>Ver detalle del proveedor</Text>
              <IcomoonIcon name='chevron-right' color={tw.color('secondary')} style={tw`ml-1`} />
            </TouchableOpacity>
          </Card>
        </View>
      </View>


      


      {/*

        Datos cuenta origen

      */}
      <Card title="Datos cuenta de AC (cuenta origen)">
        <AttributeRow label={"Tipo"} value={paymentRequest.community_bank_account?.name} />
        <AttributeRow label={"Banco"} value={paymentRequest.community_bank_account?.bank} />
        <AttributeRow label={"No. de cuenta"} value={paymentRequest.community_bank_account?.account_number} />
        <AttributeRow label={"Sucursal"} value={paymentRequest.community_bank_account?.sucursal} />
        <AttributeRow label={"CLABE"} value={paymentRequest.community_bank_account?.clabe} last />
      </Card>


      {/* Subir documento de soporte */}
      <View style={tw``}>
        <DocumentPicker
          onFileSelected={(file) => {
            console.log("Newpaymentrequest", file)
            toast.show(`Documento de soporte cargado correctamente`, { type: "success"})
            refetch()
          }}
          postUrl={`/vinte/payment-requests/${paymentRequestId}/documents`}
          acceptedMimes
          label={"Subir documento de soporte"}
          resetOnSuccess={true}
        />
      </View>


      {/*

        Documentos de soporte

      */}
      <Card
        title="Documentos de soporte"
        right={
          <TouchableOpacity
            style={tw`row items-center bg-secondary rounded-lg px-3 py-2`}
            onPress={() => setShowPrintDocumentsModal(true)}
          >
            <Text style={tw`text-white text-sm`}>Descargar paquete</Text>
          </TouchableOpacity>
        }
      >
        <View style={tw``}>
          {
            paymentRequest.cfdi.xml_url &&
              <FileButton url={paymentRequest.cfdi.xml_url} name={"Factura"} subtitle={"Archivo XML"} type={"xml"} />
          }

          {
            paymentRequest.cfdi.pdf_url &&
              <FileButton url={paymentRequest.cfdi.pdf_url} name={"Factura"} subtitle={"Archivo PDF"} type={"pdf"} />
          }
          {
            paymentRequest.documents?.map((document: any) =>
              <FileButton
                url={document.url}
                name={document.name}
                subtitle={document.file_type == "image" ? 'Imagen' : `Archivo ${document.file_type}`}
                type={document.file_type}
              />
            )
          }
        </View>
      </Card>


      {/*

        Botones aprobación

      */}
      {
        can_approve &&
          <View
            style={[
              tw`row justify-between gap-x-3 bg-white border-t border-gray-100 px-6 py-4 -mx-6 -mb-6`,
              isSubmitting && tw`opacity-50`,
              { position: 'sticky', bottom: 0 } as any,
            ]}
          >
            {/* Rechazar: outline rojo, fondo blanco */}
            <TouchableOpacity
              style={tw`flex-1 rounded-xl py-3 items-center justify-center bg-white border border-red-500`}
              onPress={() => setShowRejectModal(true)}
              disabled={isSubmitting}
            >
              <Text style={tw`text-red-500 font-600`}>Rechazar</Text>
            </TouchableOpacity>

            {/* Validar: primary lleno */}
            <TouchableOpacity
              style={tw`flex-1 rounded-xl py-3 items-center justify-center bg-primary`}
              onPress={approve}
              disabled={isSubmitting}
            >
              <Text style={tw`text-white font-600`}>Validar</Text>
            </TouchableOpacity>
          </View>
      }

      {
        showRejectModal &&
          <PaymentRequestApprovalRejectBottomSheet
            paymentRequestId={paymentRequestId}
            onRequestClose={() => setShowRejectModal(false)}
            onPaymentRequestRejected={() => {
              setShowRejectModal(false)
              refetch()
            }}
            visible={true}
          />
      }

      {
        showPrintDocumentsModal &&
          <PrintDocumentsModal
            documents={paymentRequest.documents}
            onRequestClose={() => setShowPrintDocumentsModal(false)}
          />
      }
    </View>
  )
}
